import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Box
} from '@mui/material';
import { SearchIcon } from '@modules/casting/components/casting-icons/casting-icons.tsx';

interface SearchableTableProps {
  columns: Array<{
    id: string;
    label: string;
    field: string;
    render: (value: any, row: any) => React.ReactNode;
    showSearch?: boolean;
    showSort?: boolean;
    searchValue?: (value: any, row: any) => string; // Custom function to get searchable value
    width?: number; // Column width in pixels
  }>;
  data: any[];
  searchPlaceholder?: string;
  emptyMessage?: string;
}

const SearchableTable: React.FC<SearchableTableProps> = ({
  columns,
  data,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No data found'
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Helper function to get nested values from object
  const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  };

  const searchableColumns = useMemo(
    () => columns.filter((col) => col.showSearch !== false),
    [columns]
  );

  const filteredAndSortedData = useMemo(() => {
    let filtered = data;

    // Apply search
    if (searchTerm) {
      filtered = data.filter((row) =>
        searchableColumns.some((col) => {
          const rawValue = getNestedValue(row, col.field);
          if (rawValue === null || rawValue === undefined) return false;
          
          // Use custom searchValue function if provided, otherwise use raw value
          const searchValue = col.searchValue 
            ? col.searchValue(rawValue, row)
            : rawValue.toString();
          
          return searchValue.toLowerCase().includes(searchTerm.toLowerCase());
        })
      );
    }

    // Apply sort
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = getNestedValue(a, sortColumn);
        const bValue = getNestedValue(b, sortColumn);

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Handle different types
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          const comparison = aValue.localeCompare(bValue);
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          const comparison = aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
          return sortDirection === 'asc' ? comparison : -comparison;
        }

        // Fallback to string comparison
        const aStr = String(aValue);
        const bStr = String(bValue);
        const comparison = aStr.localeCompare(bStr);
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return filtered;
  }, [data, searchTerm, sortColumn, sortDirection, searchableColumns]);

  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  return (
    <Box>
      <Box className="mb-3">
        <TextField
          fullWidth
          size="small"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" className="!text-gray-400" />
              </InputAdornment>
            )
          }}
        />
      </Box>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  className="!font-semibold"
                  style={{ 
                    cursor: column.showSort !== false ? 'pointer' : 'default',
                    width: column.width ? `${column.width}px` : 'auto',
                    minWidth: column.width ? `${column.width}px` : 'auto'
                  }}
                  onClick={() => column.showSort !== false && handleSort(column.field)}
                >
                  <div className="flex items-center gap-1">
                    {column.label}
                    {column.showSort !== false && sortColumn === column.field && (
                      <span className="text-xs">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedData.length > 0 ? (
              filteredAndSortedData.map((row, index) => (
                <TableRow key={index} hover>
                  {columns.map((column) => (
                    <TableCell 
                      key={column.id}
                      style={{ 
                        width: column.width ? `${column.width}px` : 'auto',
                        minWidth: column.width ? `${column.width}px` : 'auto'
                      }}
                    >
                      {column.render(getNestedValue(row, column.field), row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" className="!py-8">
                  <span className="!opacity-50">{emptyMessage}</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SearchableTable;

